import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import AppointmentForm from '../components/AppointmentForm';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { useAuth } from '../contexts/AuthContext';
import { disconnectAppSocket, getAppSocket } from '../lib/wesSocket';
import {
  CalendarIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  UserIcon,
  PlusIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  ListBulletIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  XMarkIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';
import { ChevronDownIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DatePicker } from '../components/ui/date-picker';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tip } from '../components/ui/tip';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';
import { confirmDialog } from '../components/ConfirmDialog';
import ClientDataDialog from '../components/ClientDataDialog';
import VehiclePlateHover from '../components/VehiclePlateHover';
import { hoyLocal, toYMD } from '../utils/fechaLocal';

import InformeServicioDialog from '../components/informes/InformeServicioDialog';
import { useRetiro } from '../components/RetiroDialog';

// Interfaces mejoradas
interface AppointmentEnhanced {
  id: string;
  appointmentCode?: string;
  clientId: string;
  clientName: string;
  clientPhone: string;
  vehicleId?: string;
  vehiclePlate?: string;
  vehicleBrand?: string;
  vehicleModel?: string;
  vehicleYear?: number | string;
  vehicleEngine?: string;
  vehicleColor?: string;
  vehicleFuel?: string;
  vehicleMileage?: number;
  vehicleVin?: string;
  serviceType: string;
  scheduledDate: string;
  timeSlot?: string;
  estimatedDuration: number;
  actualDuration?: number;
  assignedMechanic?: string;
  mechanicName?: string;

  status: 'pending' | 'confirmed' | 'in_progress' | 'waiting_parts' | 'ready_for_pickup' | 'completed' | 'cancelled';
  notes?: string;
  products?: any[];
  mileage?: number;
  oilChangeAndFilter?: boolean;
  totalCost?: number;
  paymentStatus: 'pending' | 'partial' | 'paid';
  startedAt?: string;
  completedAt?: string;
  statusConfig?: {
    color: string;
    bgColor: string;
    textColor: string;
    label: string;
    description: string;
  };

}

interface StatusConfig {
  value: string;
  color: string;
  bgColor: string;
  textColor: string;
  label: string;
  description: string;
}

// DatePicker component for Appointments Calendar
const DatePickerCalendar = ({ value, onChange, id }: { value?: string; onChange: (date: string) => void; id?: string }) => {
  const [open, setOpen] = useState(false);

  // Parsear la fecha correctamente para evitar problemas de zona horaria
  const dateValue = value ? (() => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  })() : undefined;

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      // Formatear la fecha correctamente en zona horaria local
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      onChange(`${year}-${month}-${day}`);
      setOpen(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id={id}
          className={cn(
            "w-auto justify-between font-normal h-10 rounded-lg",
            !dateValue && "text-muted-foreground"
          )}
        >
          {dateValue ? dateValue.toLocaleDateString('es-AR', {
            weekday: 'long',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }).replace(/^\w/, c => c.toUpperCase()) : 'Seleccionar fecha'}
          <ChevronDownIcon className="ml-2 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          captionLayout="dropdown"
          fromYear={currentYear - 1}
          toYear={currentYear + 2}
        />
      </PopoverContent>
    </Popover>
  );
};

const displayClientName = (apt: any) => {
  const name = String(apt?.clientName || '').trim();
  if (name === 'Sin datos' && apt?.vehiclePlate) return `Sin datos · ${apt.vehiclePlate}`;
  return name;
};

const AppointmentsEnhanced: React.FC = () => {
  // Los clientes "Sin datos" (creados solo con la patente) se identifican por su vehiculo
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isDarkMode } = useTheme();
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { pedirRetiro, dialogoRetiro } = useRetiro();

  // Permisos
  const canViewSchedule = hasPermission('view_schedule') || hasPermission('appointments');
  const canViewAllDates = hasPermission('reports');
  // Puede crear turnos: admin/cajero (reports) o quien NO sea operario de piso (sin wes_operator)
  const canCreateAppointments = hasPermission('reports') || !hasPermission('wes_operator');
  const canViewPrices = hasPermission('view_prices') || hasPermission('reports');
  const canEditClients = hasPermission('clients');
  // Completar/corregir los datos del cliente del turno (o vincular un "Sin datos")
  const [clientDataFor, setClientDataFor] = useState<string | null>(null);
  // El botón de datos solo aparece donde FALTA algo: cliente "Sin datos" (creado
  // con la patente) o sin teléfono. Si está completo, no ensucia la fila.
  const needsClientData = (apt: any) =>
    canEditClients && !!apt?.clientId &&
    (String(apt.clientName || '').trim() === 'Sin datos' || !String(apt.clientPhone || '').trim());

  // Fin de semana actual (domingo)
  const getEndOfWeek = (): string => {
    const d = new Date();
    const day = d.getDay(); // 0=dom, 1=lun, ...6=sab
    const daysToSunday = day === 0 ? 0 : 7 - day;
    d.setDate(d.getDate() + daysToSunday);
    return toYMD(d);
  };

  // Estados principales
  const [appointments, setAppointments] = useState<AppointmentEnhanced[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('list');
  const [selectedDate, setSelectedDate] = useState(hoyLocal());
  const [showPast, setShowPast] = useState(false);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Estados para configuraciones
  const [statusConfigs, setStatusConfigs] = useState<StatusConfig[]>([]);

  // Estados para filtros
  const [filters, setFilters] = useState({
    status: '',
    mechanic: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [mechanics, setMechanics] = useState<{ id: string; name: string }[]>([]);

  // Un rango de fechas manda sobre el "desde hoy" por defecto y abre el historial
  const usaRangoPropio = Boolean(filters.dateFrom || filters.dateTo);
  const hayFiltros = Boolean(filters.status || filters.mechanic || filters.dateFrom || filters.dateTo || filters.search);
  const limpiarFiltros = () =>
    setFilters({ status: '', mechanic: '', dateFrom: '', dateTo: '', search: '' });

  useEffect(() => {
    const query = new URLSearchParams(location.search).get('q');
    if (query && query !== filters.search) {
      setFilters((prev) => ({ ...prev, search: query }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Estados para modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentEnhanced | null>(null);
  // Links viejos con ?clientId=… (desde presupuesto): el alta ahora es una
  // pantalla propia, se redirige con los mismos parámetros
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('clientId')) {
      navigate(`/appointments-enhanced/new?${params.toString()}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Vista de calendario por slots de tiempo
  const [calendarData, setCalendarData] = useState<{[key: string]: AppointmentEnhanced[]}>({});

  // Slots de tiempo predefinidos (incluye mediodía)
  const timeSlots = [
    '08:00-09:00', '09:00-10:00', '10:00-11:00', '11:00-12:00',
    '12:00-13:00', '13:00-14:00',
    '14:00-15:00', '15:00-16:00', '16:00-17:00', '17:00-18:00'
  ];

  const breakSlots = new Set(['12:00-13:00', '13:00-14:00']);

  // Estado para vista semanal
  const [calendarMode, setCalendarMode] = useState<'day' | 'week' | 'month'>('day');
  const [weekData, setWeekData] = useState<{[date: string]: {[slot: string]: AppointmentEnhanced[]}}>({});
  const [monthData, setMonthData] = useState<{[date: string]: AppointmentEnhanced[]}>({});

  // Línea roja de hora actual
  const [nowLineTop, setNowLineTop] = useState<number | null>(null);

  useEffect(() => {
    const updateNowLine = () => {
      const today = hoyLocal();
      if (selectedDate !== today || calendarMode !== 'day') { setNowLineTop(null); return; }
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      const totalMin = h * 60 + m;
      // Calcular posición relativa dentro de los slots
      let accPx = 0;
      for (const slot of timeSlots) {
        const [start] = slot.split('-');
        const [sh, sm] = start.split(':').map(Number);
        const slotStart = sh * 60 + sm;
        const slotEnd = slotStart + 60;
        const slotH = breakSlots.has(slot) ? 40 : 48; // slots de almuerzo más chicos si vacíos
        const slotsApts = calendarData[slot] || [];
        const realH = slotsApts.length > 0 ? Math.max(slotH, 72 + (slotsApts.length - 1) * 56) : slotH;
        if (totalMin >= slotStart && totalMin < slotEnd) {
          const pct = (totalMin - slotStart) / 60;
          setNowLineTop(accPx + pct * realH);
          return;
        }
        accPx += realH;
      }
      setNowLineTop(null);
    };
    updateNowLine();
    const timer = setInterval(updateNowLine, 60000);
    return () => clearInterval(timer);
  }, [selectedDate, calendarMode, calendarData]);

  // Fetch semana para vista semanal
  const fetchWeekData = useCallback(async (baseDate: string) => {
    try {
      const [y, mo, d] = baseDate.split('-').map(Number);
      const base = new Date(y, mo - 1, d);
      const dayOfWeek = base.getDay();
      const monday = new Date(base);
      monday.setDate(base.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

      const promises = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(monday);
        day.setDate(monday.getDate() + i);
        const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
        promises.push(
          axios.get(`/api/appointments-enhanced/calendar/${dateStr}`)
            .then(res => ({ date: dateStr, data: res.data?.timeSlots || {} }))
            .catch(() => ({ date: dateStr, data: {} }))
        );
      }
      const results = await Promise.all(promises);
      const wData: {[date: string]: {[slot: string]: AppointmentEnhanced[]}} = {};
      for (const r of results) wData[r.date] = r.data;
      setWeekData(wData);
    } catch { /* silenciar */ }
  }, []);

  useEffect(() => {
    if (calendarMode === 'week' && viewMode === 'calendar') {
      fetchWeekData(selectedDate);
    }
  }, [calendarMode, selectedDate, viewMode, fetchWeekData]);

  // Días de la grilla mensual (6 semanas x 7 = 42, empezando lunes)
  const getMonthGridDays = useCallback((baseDate: string) => {
    const [y, mo] = baseDate.split('-').map(Number);
    const first = new Date(y, mo - 1, 1);
    const dow = first.getDay(); // 0=Dom
    const start = new Date(first);
    start.setDate(first.getDate() - (dow === 0 ? 6 : dow - 1)); // retroceder al lunes
    const days: { date: string; inMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      days.push({ date: ds, inMonth: d.getMonth() === mo - 1 });
    }
    return days;
  }, []);

  // Fetch mes para vista mensual (aplana timeSlots a una lista por día)
  const fetchMonthData = useCallback(async (baseDate: string) => {
    try {
      const days = getMonthGridDays(baseDate);
      const results = await Promise.all(days.map(({ date }) =>
        axios.get(`/api/appointments-enhanced/calendar/${date}`)
          .then(res => ({ date, slots: res.data?.timeSlots || {} }))
          .catch(() => ({ date, slots: {} }))
      ));
      const mData: {[date: string]: AppointmentEnhanced[]} = {};
      for (const r of results) {
        const list: AppointmentEnhanced[] = [];
        Object.values(r.slots).forEach((arr: any) => { if (Array.isArray(arr)) list.push(...arr); });
        list.sort((a: any, b: any) => String(a.timeSlot || '').localeCompare(String(b.timeSlot || '')));
        mData[r.date] = list;
      }
      setMonthData(mData);
    } catch { /* silenciar */ }
  }, [getMonthGridDays]);

  useEffect(() => {
    if (calendarMode === 'month' && viewMode === 'calendar') {
      fetchMonthData(selectedDate);
    }
  }, [calendarMode, selectedDate, viewMode, fetchMonthData]);

  const shiftSelectedMonth = (delta: number) => {
    const [year, month] = selectedDate.split('-').map(Number);
    const d = new Date(year, month - 1 + delta, 1);
    setSelectedDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
  };

  const shiftSelectedDate = (days: number) => {
    const [year, month, day] = selectedDate.split('-').map(Number);
    const baseDate = new Date(year, month - 1, day);
    baseDate.setDate(baseDate.getDate() + days);
    const nextYear = baseDate.getFullYear();
    const nextMonth = String(baseDate.getMonth() + 1).padStart(2, '0');
    const nextDay = String(baseDate.getDate()).padStart(2, '0');
    setSelectedDate(`${nextYear}-${nextMonth}-${nextDay}`);
  };

  // Función para verificar si un slot ya pasó la hora actual
  const isSlotExpired = (timeSlot: string, selectedDate: string) => {
    const today = toYMD(currentTime);
    
    // Solo verificar si es hoy
    if (selectedDate !== today) return false;
    
    // Extraer la hora de inicio del slot (ej: "08:00-09:00" -> "08:00")
    const startTime = timeSlot.split('-')[0];
    const [startHour, startMinute] = startTime.split(':').map(Number);
    
    // Comparar con la hora actual
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();
    
    // Convertir a minutos para comparación fácil
    const slotMinutes = startHour * 60 + startMinute;
    const currentMinutes = currentHour * 60 + currentMinute;
    
    return currentMinutes > slotMinutes;
  };

  // Agregar estado para edición
  const [editAppointment, setEditAppointment] = useState<any>(null);

  // Estados para selección múltiple y acciones masivas
  const [selectedAppointments, setSelectedAppointments] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  // Estado para forzar re-render cuando cambia la hora
  const [currentTime, setCurrentTime] = useState(new Date());
  const refreshTimeoutRef = useRef<number | null>(null);

  // Helper: formatea fecha local cuando viene como YYYY-MM-DD para evitar desfase de un día
  const formatLocalDate = (value?: string) => {
    if (!value) return '-';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-');
      return `${d}/${m}/${y}`;
    }
    try {
      return new Date(value).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (_) {
      return value;
    }
  };

  // Cargar datos iniciales
  const fetchAppointments = useCallback(async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent);
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const params = new URLSearchParams();
      if (viewMode === 'calendar') {
        params.append('date', selectedDate);
      } else if (!canViewAllDates) {
        // Operario y roles sin reports: solo hoy + resto de la semana
        const today = hoyLocal();
        params.append('dateFrom', today);
        params.append('dateTo', getEndOfWeek());
      } else if (usaRangoPropio) {
        // Si el usuario eligió un rango, ese manda (y ya incluye el pasado)
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
        params.append('order', 'desc');
      } else if (!showPast) {
        // Admin/cajero en modo lista: desde hoy por defecto (pueden ver historial con el toggle)
        const today = hoyLocal();
        params.append('dateFrom', today);
      } else {
        // Historial completo: lo más reciente primero
        params.append('order', 'desc');
      }
      if (filters.status) params.append('status', filters.status);
      if (filters.mechanic) params.append('assignedMechanic', filters.mechanic);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`/api/appointments-enhanced?${params}`);
      setAppointments(response.data.appointments);

      // Si está en modo calendario, obtener datos organizados por slots
      if (viewMode === 'calendar') {
        const calendarResponse = await axios.get(`/api/appointments-enhanced/calendar/${selectedDate}`);
        setCalendarData(calendarResponse.data.timeSlots);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      if (!silent) {
        setError('No se pudieron cargar los turnos. Por favor, intenta nuevamente.');
        toast.error('Error al cargar los turnos');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [viewMode, selectedDate, filters, showPast, usaRangoPropio]);

  // Cargar configuraciones
  const fetchConfigurations = useCallback(async () => {
    try {
      const statusResponse = await axios.get('/api/appointments-enhanced/status-config');
      setStatusConfigs(statusResponse.data.statuses);
    } catch (error) {
      console.error('Error fetching configurations:', error);
    }
  }, []);

  // Mecánicos para el filtro (los que efectivamente trabajan sobre el vehículo)
  useEffect(() => {
    if (!canViewAllDates) return;
    axios.get('/api/users')
      .then((res) => {
        const list = (res.data.users || res.data || [])
          .filter((u: any) => u.isActive !== false && u.isActive !== 0)
          .filter((u: any) => ['operario', 'admin'].includes(String(u.role || '').toLowerCase()))
          .map((u: any) => ({ id: u.id, name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username }));
        setMechanics(list);
      })
      .catch(() => {});
  }, [canViewAllDates]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  useEffect(() => {
    const socket = getAppSocket();
    const scheduleSilentRefresh = (payload?: any) => {
      const path = payload?.path ? String(payload.path) : '';
      if (path && !(
        path.startsWith('/api/appointments') ||
        path.startsWith('/api/appointments-enhanced') ||
        path.startsWith('/api/service-reports')
      )) {
        return;
      }

      if (refreshTimeoutRef.current) window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = window.setTimeout(() => {
        fetchAppointments({ silent: true });
      }, 400);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
      disconnectAppSocket();
    };
  }, [fetchAppointments]);

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  // Actualizar la hora cada minuto para tachar slots vencidos
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Actualizar cada minuto

    return () => clearInterval(interval);
  }, []);

  // Atajo de teclado para crear nuevo turno
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'n' || e.key === 'N') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA' && !target.isContentEditable) {
          e.preventDefault();
          navigate('/appointments-enhanced/new');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // Función para limpiar selección
  const clearSelection = () => {
    setSelectedAppointments(new Set());
    setSelectAll(false);
    setShowBulkActions(false);
  };

  // Función para cambiar estado de múltiples turnos
  const handleBulkStatusChange = async (newStatus: string) => {
    try {
      await Promise.all(
        Array.from(selectedAppointments).map(id =>
          axios.patch(`/api/appointments-enhanced/${id}`, { status: newStatus })
        )
      );
      toast.success('Estados actualizados correctamente');
      fetchAppointments();
      clearSelection();
    } catch (error) {
      console.error('Error updating appointments:', error);
      toast.error((error as any)?.response?.data?.error || 'Error al actualizar los turnos');
    }
  };

  // Función para eliminar múltiples turnos
  const handleDeleteAppointment = async (id: string) => {
    if (!await confirmDialog('¿Eliminar este turno? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`/api/appointments-enhanced/${id}`);
      toast.success('Turno eliminado');
      fetchAppointments();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar el turno');
    }
  };

  // Marcar el turno como entregado (el cliente retiró el vehículo). Cierra la OT
  // y, si corresponde, programa el pedido de reseña al cliente.
  const handleDeliver = async (apt: any) => {
    const code = apt.appointmentCode || 'este turno';
    // Clientes que piden quién retira (Grumaq): el cartel con nombre y DNI ya es la confirmación
    const retiro = await pedirRetiro(apt.id, [apt.clientName, apt.vehiclePlate, apt.appointmentCode].filter(Boolean).join(' · '));
    if (retiro === undefined) return;
    if (retiro === null && !await confirmDialog(`¿Marcar ${code} como entregado?\n\nEl vehículo sale del taller y, si el cliente nunca recibió uno, se programa el pedido de reseña por WhatsApp.`)) return;
    try {
      await axios.post(`/api/reception/${apt.id}/deliver`, retiro ? { retiro } : {});
      toast.success('Turno marcado como entregado');
      fetchAppointments();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo marcar como entregado');
    }
  };

  const handleBulkDelete = async () => {
    if (!await confirmDialog(`¿Eliminar ${selectedAppointments.size} turno(s)?`)) return;

    try {
      await Promise.all(
        Array.from(selectedAppointments).map(id =>
          axios.delete(`/api/appointments-enhanced/${id}`)
        )
      );
      toast.success('Turnos eliminados correctamente');
      fetchAppointments();
      clearSelection();
    } catch (error) {
      console.error('Error deleting appointments:', error);
      toast.error('Error al eliminar los turnos');
    }
  };

  // Recepción: el sistema detecta automáticamente la estación según el servicio
  const [receptionLoading, setReceptionLoading] = useState<string | null>(null);
  // El km del tablero ya no se pide al recepcionar ni al iniciar (16/10/2026):
  // se ofrece, opcional, al completar el trabajo en Taller

  const handleReception = async (appointment: AppointmentEnhanced) => {
    setReceptionLoading(appointment.id);
    try {
      // Sin stationKeys → backend autodetecta
      const res = await axios.post(`/api/reception/check-in/${appointment.id}`, {});
      // El backend devuelve la cola de estaciones. Mostrar toast informativo.
      const stationsRes = await axios.get('/api/reception/stations');
      const stations = stationsRes.data.stations || [];
      const queue = res.data.stationQueue || [];
      const labels = queue.map((k: string) => stations.find((s: any) => s.key === k)?.name || k).join(' → ');
      toast.success(`Vehículo recepcionado: ${labels}`);
      await fetchAppointments();
    } catch (err: any) {
      console.error('Error al recepcionar:', err);
      toast.error(err.response?.data?.error || 'Error al recepcionar el vehículo');
    } finally {
      setReceptionLoading(null);
    }
  };

  const handleStartService = async (appointment: AppointmentEnhanced) => {
    try {
      // Cambiar el estado del turno a "en progreso" (in_progress)
      await axios.patch(`/api/appointments-enhanced/${appointment.id}`, { status: 'in_progress' });

      toast.success('Servicio iniciado correctamente');
      await fetchAppointments();
    } catch (err: any) {
      console.error('Error starting service:', err);
      toast.error(err.response?.data?.error || 'Error al iniciar el servicio');
    }
  };

  // Informe de servicio (reemplaza al "Documento de Servicio", v12.3.0)
  const [informeOtId, setInformeOtId] = useState<string | null>(null);

  // Resetear página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.search, filters.status, filters.mechanic, filters.dateFrom, filters.dateTo, selectedDate]);

  // Cálculos de paginación
  const totalResults = appointments.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAppointments = appointments.slice(startIndex, endIndex);
  const startResult = totalResults === 0 ? 0 : startIndex + 1;
  const endResult = Math.min(endIndex, totalResults);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Funciones de navegación de paginación
  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);

  // Vista de calendario
  // Mini resumen del día
  const daySummary = React.useMemo(() => {
    const all: AppointmentEnhanced[] = [];
    for (const slot of timeSlots) {
      all.push(...(calendarData[slot] || []));
    }
    return {
      total: all.length,
      pending: all.filter(a => a.status === 'pending' || a.status === 'confirmed').length,
      inProgress: all.filter(a => a.status === 'in_progress').length,
      completed: all.filter(a => a.status === 'completed').length,
    };
  }, [calendarData, timeSlots]);

  // Helper para obtener lunes de la semana
  const getWeekDays = (baseDate: string) => {
    const [y, mo, d] = baseDate.split('-').map(Number);
    const base = new Date(y, mo - 1, d);
    const dayOfWeek = base.getDay();
    const monday = new Date(base);
    monday.setDate(base.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const days: { date: string; label: string; isToday: boolean }[] = [];
    const today = hoyLocal();
    const dayNames = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
    for (let i = 0; i < 7; i++) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
      days.push({ date: dateStr, label: `${dayNames[i]} ${day.getDate()}`, isToday: dateStr === today });
    }
    return days;
  };

  const renderCalendarView = () => {
    const today = hoyLocal();
    const isToday = selectedDate === today;

    return (
    <section className="rounded-3xl border border-border/50 bg-card p-6 shadow-sm lg:p-8">
      <div className="space-y-4">
        {/* Header con navegación y toggle */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-foreground">Calendario de Turnos</h3>
          <div className="flex flex-wrap items-center gap-2">
            {/* Toggle Día/Semana */}
            <div className="inline-flex h-8 items-center rounded-lg bg-muted p-1">
              <button
                type="button"
                onClick={() => setCalendarMode('day')}
                className={`inline-flex items-center rounded-md px-3 py-1 text-xs font-medium transition-all ${calendarMode === 'day' ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Dia
              </button>
              <button
                type="button"
                onClick={() => setCalendarMode('week')}
                className={`inline-flex items-center rounded-md px-3 py-1 text-xs font-medium transition-all ${calendarMode === 'week' ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Semana
              </button>
              <button
                type="button"
                onClick={() => setCalendarMode('month')}
                className={`inline-flex items-center rounded-md px-3 py-1 text-xs font-medium transition-all ${calendarMode === 'month' ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Mes
              </button>
            </div>
            <Button type="button" variant="outline" size="icon" onClick={() => calendarMode === 'month' ? shiftSelectedMonth(-1) : shiftSelectedDate(calendarMode === 'week' ? -7 : -1)}>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" onClick={() => calendarMode === 'month' ? shiftSelectedMonth(1) : shiftSelectedDate(calendarMode === 'week' ? 7 : 1)}>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
            {isToday ? null : (
              <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedDate(today)} className="text-xs">
                Hoy
              </Button>
            )}
            <DatePickerCalendar value={selectedDate} onChange={setSelectedDate} id="calendar-date-picker" />
          </div>
        </div>

        {/* Mini resumen del día */}
        {calendarMode === 'day' && daySummary.total > 0 && (
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-1.5 rounded-md bg-muted/50 px-3 py-1.5 text-xs font-medium">
              <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{daySummary.total} turnos</span>
            </div>
            {daySummary.pending > 0 && (
              <div className="flex items-center gap-1.5 rounded-md bg-amber-50 dark:bg-amber-950/30 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                <ClockIcon className="h-3.5 w-3.5" />
                <span>{daySummary.pending} pendientes</span>
              </div>
            )}
            {daySummary.inProgress > 0 && (
              <div className="flex items-center gap-1.5 rounded-md bg-blue-50 dark:bg-blue-950/30 px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-400">
                <WrenchScrewdriverIcon className="h-3.5 w-3.5" />
                <span>{daySummary.inProgress} en progreso</span>
              </div>
            )}
            {daySummary.completed > 0 && (
              <div className="flex items-center gap-1.5 rounded-md bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                <CheckIcon className="h-3.5 w-3.5" />
                <span>{daySummary.completed} completados</span>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <Alert>
            <ClockIcon className="h-4 w-4" />
            <AlertTitle>Cargando turnos...</AlertTitle>
            <AlertDescription>Estamos obteniendo la informacion de tus turnos programados.</AlertDescription>
          </Alert>
        ) : error ? (
          <Alert variant="destructive">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <AlertTitle>Error al cargar turnos</AlertTitle>
            <AlertDescription>
              {error}
              <Button variant="link" onClick={() => fetchAppointments()} className="ml-2 h-auto p-0 text-destructive underline">Reintentar</Button>
            </AlertDescription>
          </Alert>
        ) : calendarMode === 'week' ? (
          /* ========== VISTA SEMANAL ========== */
          <div className="rounded-2xl border border-border/60 bg-background overflow-hidden overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Header de días. Las columnas son minmax(0,1fr) y NO 1fr: con 1fr el
                  mínimo de cada columna es el contenido, y un turno con nombre largo
                  ensanchaba su columna solo en esa fila (cada fila es su propia
                  grilla), así que las líneas verticales no coincidían entre horas. */}
              <div className="grid grid-cols-[60px_repeat(7,minmax(0,1fr))] border-b border-border/60">
                <div className="border-r border-border/40 bg-muted/30 p-2" />
                {getWeekDays(selectedDate).map(day => (
                  <button
                    key={day.date}
                    type="button"
                    onClick={() => { setSelectedDate(day.date); setCalendarMode('day'); }}
                    className={`border-r border-border/40 p-2 text-center text-xs font-medium transition-colors hover:bg-accent/50 ${day.isToday ? 'bg-primary/10 text-primary font-bold' : 'text-muted-foreground'} ${day.date === selectedDate ? 'bg-accent' : ''}`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
              {/* Slots */}
              {timeSlots.filter(s => !breakSlots.has(s)).map(slot => {
                const [startTime] = slot.split('-');
                return (
                  <div key={slot} className="grid grid-cols-[60px_repeat(7,minmax(0,1fr))] border-b border-border/40">
                    <div className="border-r border-border/40 bg-muted/30 px-2 py-1 text-[10px] font-medium text-muted-foreground flex items-start pt-2">
                      {startTime}
                    </div>
                    {getWeekDays(selectedDate).map(day => {
                      const daySlots = weekData[day.date] || {};
                      const apts = daySlots[slot] || [];
                      return (
                        <div
                          key={day.date}
                          className={`border-r border-border/40 min-h-[40px] min-w-0 overflow-hidden p-1 ${apts.length === 0 && canCreateAppointments ? 'cursor-pointer hover:bg-accent/30' : ''}`}
                          onClick={() => {
                            if (apts.length === 0 && canCreateAppointments) {
                              navigate(`/appointments-enhanced/new?date=${day.date}&slot=${encodeURIComponent(slot)}`);
                            }
                          }}
                        >
                          {apts.map(apt => (
                            <button
                              key={apt.id}
                              type="button"
                              className="block w-full min-w-0 overflow-hidden mb-0.5 rounded px-1.5 py-1 text-left text-[10px] leading-tight shadow-sm border transition hover:opacity-80"
                              style={{
                                backgroundColor: apt.statusConfig?.bgColor || '#F8FAFC',
                                color: apt.statusConfig?.textColor || '#0F172A',
                                borderColor: apt.statusConfig?.color || 'transparent'
                              }}
                              onClick={(e) => { e.stopPropagation(); setSelectedDate(day.date); setCalendarMode('day'); setSelectedAppointment(apt); }}
                            >
                              <div className="font-semibold truncate">{displayClientName(apt)}</div>
                              <div className="truncate opacity-75">{apt.serviceType}</div>
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        ) : calendarMode === 'month' ? (
          /* ========== VISTA MENSUAL ========== */
          <div className="rounded-2xl border border-border/60 bg-background overflow-hidden">
            <div className="grid grid-cols-7 border-b border-border/60 bg-muted/30">
              {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d) => (
                <div key={d} className="p-2 text-center text-xs font-medium text-muted-foreground">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {getMonthGridDays(selectedDate).map(({ date, inMonth }) => {
                const list = monthData[date] || [];
                const isTodayCell = date === today;
                return (
                  <button
                    key={date}
                    type="button"
                    onClick={() => { setSelectedDate(date); setCalendarMode('day'); }}
                    className={`min-h-[96px] border-b border-r border-border/40 p-1.5 text-left align-top transition-colors hover:bg-accent/50 ${inMonth ? '' : 'bg-muted/20'}`}
                  >
                    <div className="mb-1">
                      <span className={`text-xs font-medium ${isTodayCell ? 'inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground' : inMonth ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {Number(date.slice(8, 10))}
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      {list.slice(0, 3).map((apt: any) => (
                        <div key={apt.id} className="flex items-center gap-1 text-[10px] truncate">
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${apt.status === 'completed' ? 'bg-emerald-500' : apt.status === 'in_progress' ? 'bg-blue-500' : apt.status === 'cancelled' ? 'bg-red-500' : apt.status === 'ready_for_pickup' ? 'bg-emerald-600' : 'bg-amber-500'}`} />
                          <span className="truncate">{apt.timeSlot ? apt.timeSlot.slice(0, 5) + ' ' : ''}{displayClientName(apt) || apt.companyName || `${apt.firstName || ''} ${apt.lastName || ''}`.trim() || 'Turno'}</span>
                        </div>
                      ))}
                      {list.length > 3 && <div className="text-[10px] text-muted-foreground">+{list.length - 3} más</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          /* ========== VISTA DIARIA ========== */
          <div className="rounded-2xl border border-border/60 bg-background overflow-hidden relative">
            {/* Línea roja de hora actual */}
            {nowLineTop !== null && (
              <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: `${nowLineTop}px` }}>
                <div className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-red-500 -ml-1" />
                  <div className="flex-1 h-[2px] bg-red-500" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-[60px_1fr]">
              <div className="border-r border-border/40 bg-muted/30">
                {timeSlots.map((slot) => {
                  const [startTime] = slot.split('-');
                  const slotsApts = calendarData[slot] || [];
                  const isBreak = breakSlots.has(slot);
                  const isEmpty = slotsApts.length === 0;
                  return (
                    <div
                      key={`label-${slot}`}
                      className={`border-b border-border/40 px-2 py-2 text-xs font-medium text-muted-foreground flex items-start ${isEmpty && isBreak ? 'min-h-[40px]' : isEmpty ? 'min-h-[48px]' : 'min-h-[72px]'}`}
                    >
                      {startTime}
                    </div>
                  );
                })}
              </div>
              <div>
                {timeSlots.map((slot) => {
                  const slotsAppointments = calendarData[slot] || [];
                  const isExpired = isSlotExpired(slot, selectedDate);
                  const isBreak = breakSlots.has(slot);
                  const isEmpty = slotsAppointments.length === 0;
                  return (
                    <div
                      key={slot}
                      className={`border-b border-border/40 px-3 py-2 transition-colors ${
                        isBreak ? 'bg-muted/10' : isExpired ? 'bg-muted/20 text-muted-foreground' : 'bg-background'
                      } ${isEmpty && isBreak ? 'min-h-[40px]' : isEmpty ? 'min-h-[48px]' : 'min-h-[72px]'} ${isEmpty && !isBreak && canCreateAppointments ? 'cursor-pointer hover:bg-accent/30' : ''}`}
                      onClick={() => {
                        if (isEmpty && !isBreak && canCreateAppointments) {
                          navigate(`/appointments-enhanced/new?date=${selectedDate}&slot=${encodeURIComponent(slot)}`);
                        }
                      }}
                    >
                      {isEmpty ? (
                        <span className="text-xs text-muted-foreground/60 select-none">
                          {isBreak ? '' : canCreateAppointments ? 'Click para agendar' : ''}
                        </span>
                      ) : (
                        <div className="grid gap-1.5">
                          {slotsAppointments.map((apt) => (
                            <button
                              key={apt.id}
                              type="button"
                              className="w-full rounded-lg border px-3 py-2 text-left text-sm shadow-sm transition hover:shadow-md"
                              style={{
                                backgroundColor: apt.statusConfig?.bgColor || '#F8FAFC',
                                color: apt.statusConfig?.textColor || '#0F172A',
                                borderColor: apt.statusConfig?.color || 'transparent'
                              }}
                              onClick={(e) => { e.stopPropagation(); setSelectedAppointment(apt); }}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-semibold truncate">{displayClientName(apt)}</span>
                                <span
                                  className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                                  style={{
                                    backgroundColor: apt.statusConfig?.color ? `${apt.statusConfig.color}20` : '#e5e5e5',
                                    color: apt.statusConfig?.color || '#525252'
                                  }}
                                >
                                  {apt.statusConfig?.label || 'Pendiente'}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-xs opacity-80">
                                <span>{apt.serviceType}</span>
                                {apt.estimatedDuration > 0 && (
                                  <span className="flex items-center gap-0.5">
                                    <ClockIcon className="h-3 w-3" />
                                    {apt.estimatedDuration} min
                                  </span>
                                )}
                              </div>
                              {(apt.vehiclePlate || apt.vehicleModel) && (
                                <div className="text-xs mt-0.5 opacity-70">
                                  {[apt.vehiclePlate, apt.vehicleBrand, apt.vehicleModel].filter(Boolean).join(' - ')}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
    );
  };

  // Vista de lista
  const renderListView = () => (
    <>
      <Card>
        <CardContent className="p-0">
          {appointments.length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hay turnos que coincidan con los filtros</p>
            </div>
          ) : (
            <>
              <div className="hidden md:block relative w-full overflow-x-auto">
                <Table>
                <TableHeader>
                  <TableRow className="hover:bg-muted/50 border-b">
                    <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={(e) => {
                          setSelectAll(e.target.checked);
                          if (e.target.checked) {
                            setSelectedAppointments(new Set(appointments.map(a => a.id)));
                            setShowBulkActions(true);
                          } else {
                            clearSelection();
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </TableHead>
                    <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Código</TableHead>
                    <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Cliente</TableHead>
                    <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Vehículo</TableHead>
                    <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Fecha</TableHead>
                    <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Horario</TableHead>
                    <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                    <TableHead className="text-foreground h-8 px-2 text-left align-middle font-medium whitespace-nowrap"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedAppointments.map((apt) => {
                    const isSelected = selectedAppointments.has(apt.id);
                    return (
                      <TableRow 
                        key={apt.id}
                        className={`border-b hover:bg-muted/50 cursor-pointer ${isSelected ? 'bg-muted' : ''}`}
                      >
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                          <input
                            type="checkbox"
                            checked={selectedAppointments.has(apt.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              const newSelected = new Set(selectedAppointments);
                              if (e.target.checked) {
                                newSelected.add(apt.id);
                              } else {
                                newSelected.delete(apt.id);
                              }
                              setSelectedAppointments(newSelected);
                              setShowBulkActions(newSelected.size > 0);
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                          <div className="font-medium font-mono">{apt.appointmentCode || '-'}</div>
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                          <div className="font-medium">{displayClientName(apt)}</div>
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                          <div className="text-muted-foreground">
                            <VehiclePlateHover
                              plate={apt.vehiclePlate}
                              brand={apt.vehicleBrand}
                              model={apt.vehicleModel}
                              year={apt.vehicleYear}
                              engine={apt.vehicleEngine}
                              color={apt.vehicleColor}
                              fuel={apt.vehicleFuel}
                              mileage={apt.vehicleMileage}
                              vin={apt.vehicleVin}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                          <div className="text-muted-foreground">{formatLocalDate(apt.scheduledDate)}</div>
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                          <div className="text-muted-foreground">{apt.timeSlot || '-'}</div>
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                          <Badge
                            className="text-xs font-medium"
                            style={{
                              backgroundColor: apt.statusConfig?.bgColor,
                              color: apt.statusConfig?.textColor
                            }}
                          >
                            {apt.statusConfig?.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end">
                            <ButtonGroup>
                              {/* Botón Recepcionar - sólo si está pendiente/confirmado */}
                              {(apt.status === 'pending' || apt.status === 'confirmed') && (
                                <Tip label="Recepcionar vehículo"><Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReception(apt);
                                  }}
                                  className="size-8 border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                                >
                                  <TruckIcon className="h-4 w-4" />
                                </Button></Tip>
                              )}
                              {/* Botón Iniciar - solo si el turno no está en progreso, listo para entrega o completado */}
                              {apt.status !== 'in_progress' && apt.status !== 'ready_for_pickup' && apt.status !== 'completed' && apt.status !== 'cancelled' && (
                                <Tip label="Iniciar servicio"><Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartService(apt);
                                  }}
                                  className="size-8"
                                >
                                  <WrenchScrewdriverIcon className="h-4 w-4" />
                                </Button></Tip>
                              )}
                              {(apt.status === 'ready_for_pickup' || apt.status === 'in_progress') && (
                                <Tip label="Marcar entregado (el cliente retiró el vehículo)"><Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => { e.stopPropagation(); handleDeliver(apt); }}
                                  className="size-8 text-emerald-600 hover:bg-emerald-500/10"
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </Button></Tip>
                              )}
                              {apt.status !== 'completed' && apt.status !== 'cancelled' && (
                                <Tip label="Editar turno"><Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditAppointment({ ...apt, products: apt.products || [] });
                                    setShowCreateModal(true);
                                  }}
                                  className="size-8"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Button></Tip>
                              )}
                              {needsClientData(apt) && (
                                <Tip label="Faltan datos del cliente — completar o vincular a uno existente"><Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => { e.stopPropagation(); setClientDataFor(apt.clientId); }}
                                  className="size-8 border-amber-500 text-amber-600"
                                >
                                  <IdentificationIcon className="h-4 w-4" />
                                </Button></Tip>
                              )}
                              <Tip label="Ver detalles"><Button
                                variant="outline"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedAppointment(apt);
                                }}
                                className="size-8"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button></Tip>
                              {canCreateAppointments && (
                                <Tip label="Eliminar turno"><Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteAppointment(apt.id); }}
                                  className="size-8 text-destructive hover:bg-destructive/10"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button></Tip>
                              )}
                            </ButtonGroup>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>

              {/* Vista de tarjetas para pantallas pequeñas */}
              <div className="block md:hidden space-y-3">
              {paginatedAppointments.map((apt) => {
                const isSelected = selectedAppointments.has(apt.id);
                return (
                  <Card key={apt.id} className={`shadow-sm hover:shadow-md transition-shadow duration-200 ${isSelected ? 'bg-muted' : ''}`}>
                    <CardContent className="p-4">
                      <div className="mb-3">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-base truncate mb-1">{apt.appointmentCode || 'Sin código'}</h3>
                            <div className="flex items-center gap-2 flex-wrap mt-1">
                              <Badge
                                className="text-xs font-medium"
                                style={{
                                  backgroundColor: apt.statusConfig?.bgColor,
                                  color: apt.statusConfig?.textColor
                                }}
                              >
                                {apt.statusConfig?.label}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-1 mt-2">
                          <ButtonGroup>
                            {(apt.status === 'pending' || apt.status === 'confirmed') && (
                              <Tip label="Recepcionar vehículo"><Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleReception(apt)}
                                className="size-8 border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                              >
                                <TruckIcon className="h-4 w-4" />
                              </Button></Tip>
                            )}
                            {apt.status !== 'in_progress' && apt.status !== 'ready_for_pickup' && apt.status !== 'completed' && apt.status !== 'cancelled' && (
                              <Tip label="Iniciar servicio"><Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleStartService(apt)}
                                className="size-8"
                              >
                                <WrenchScrewdriverIcon className="h-4 w-4" />
                              </Button></Tip>
                            )}
                            {apt.status !== 'completed' && apt.status !== 'cancelled' && (
                              <Tip label="Editar turno"><Button
                                variant="outline"
                                size="icon"
                                onClick={() => {
                                  setEditAppointment({ ...apt, products: apt.products || [] });
                                  setShowCreateModal(true);
                                }}
                                className="size-8"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Button></Tip>
                            )}
                            {needsClientData(apt) && (
                              <Tip label="Faltan datos del cliente — completar o vincular a uno existente"><Button
                                variant="outline"
                                size="icon"
                                onClick={() => setClientDataFor(apt.clientId)}
                                className="size-8 border-amber-500 text-amber-600"
                              >
                                <IdentificationIcon className="h-4 w-4" />
                              </Button></Tip>
                            )}
                            <Tip label="Ver detalles"><Button
                              variant="outline"
                              size="icon"
                              onClick={() => setSelectedAppointment(apt)}
                              className="size-8"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Button></Tip>
                          </ButtonGroup>
                        </div>
                      </div>
                      <div className="space-y-2.5 text-sm border-t pt-3 mt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground font-medium">Cliente:</span>
                          <span className="text-foreground font-medium truncate max-w-[60%] text-right">{apt.clientName || '-'}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground font-medium">Vehículo:</span>
                          {/* En el teléfono no hay hover: marca y modelo van a la vista */}
                          <span className="text-foreground text-right truncate max-w-[60%]">
                            {apt.vehiclePlate || '-'}
                            {(apt.vehicleBrand || apt.vehicleModel) && (
                              <span className="block text-xs text-muted-foreground truncate">{[apt.vehicleBrand, apt.vehicleModel, apt.vehicleYear].filter(Boolean).join(' ')}</span>
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground font-medium">Fecha:</span>
                          <span className="text-foreground">{formatLocalDate(apt.scheduledDate)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground font-medium">Horario:</span>
                          <span className="text-foreground">{apt.timeSlot || '-'}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Paginación */}
      {!loading && totalResults > 0 && (
        <div className="flex items-center justify-between px-2">
          <div className="hidden flex-1 text-muted-foreground text-sm md:block">
            Mostrando {startResult} de {totalResults} resultados
          </div>
          <div className="flex items-center space-x-6 lg:space-x-8">
            <div className="flex w-[100px] items-center justify-center font-medium text-sm">
              Página {currentPage} de {totalPages}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="icon"
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <span className="sr-only">Ir a la primera página</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m11 17-5-5 5-5"></path>
                <path d="m18 17-5-5 5-5"></path>
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <span className="sr-only">Ir a la página anterior</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <span className="sr-only">Ir a la página siguiente</span>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <span className="sr-only">Ir a la última página</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m6 17 5-5-5-5"></path>
                <path d="m13 17 5-5-5-5"></path>
              </svg>
            </Button>
          </div>
        </div>
      )}
    </>
  );

  // Si no tiene permiso, no mostrar nada (esto no debería pasar por el middleware)
  if (!canViewSchedule) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Alert variant="destructive" className="max-w-md">
          <ExclamationTriangleIcon className="h-4 w-4" />
          <AlertTitle>Sin permisos</AlertTitle>
          <AlertDescription>
            No tienes permisos para acceder a esta sección.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Turnos</h1>

        <div className="flex gap-2.5 items-center">
          {canViewAllDates && viewMode === 'list' && !usaRangoPropio && (
            <Tip label={showPast ? 'Ocultar turnos pasados' : 'Ver historial completo'}><button
              onClick={() => setShowPast((v) => !v)}
              className={`inline-flex items-center h-8 rounded-lg px-3 text-sm font-medium border transition-colors ${showPast ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-accent'}`}
            >
              {showPast ? 'Ocultar historial' : 'Ver historial'}
            </button></Tip>
          )}
          {canCreateAppointments && (
          <Tip label="Walk-in: cliente que llegó sin turno previo (crea + recepciona)"><button
            onClick={() => navigate('/appointments-enhanced/new?immediate=1')}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium h-8 rounded-lg gap-1.5 px-3 shadow-sm transition-colors bg-cyan-600 text-white hover:bg-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <TruckIcon className="h-4 w-4 mr-1" />
            Walk-in
          </button></Tip>
          )}
          {canCreateAppointments && (
          <button
            onClick={() => navigate('/appointments-enhanced/new')}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo turno
            <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
              N
            </kbd>
          </button>
          )}
        </div>
      </div>

      {/* Vista única: Agenda (la pestaña "Turnos Activos" se reemplazó por la página /taller) */}
      {canViewSchedule ? (
        <div className="w-full">
          <div className="mt-2 space-y-6">

            {/* Buscador y Calendario/Lista */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative">
                <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
                <Input
                  type="text"
                  placeholder="Buscar por código, cliente, patente..."
                  value={filters.search}
                  onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                  className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground bg-background dark:bg-input/30 border-input flex min-w-0 rounded-lg border px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive h-8 w-full sm:w-[150px] lg:w-[250px] pl-8"
                />
              </div>

              <Tabs
                value={viewMode}
                onValueChange={(value) => setViewMode(value as 'calendar' | 'list')}
                className="w-full sm:w-auto"
              >
                <TabsList className="inline-flex h-8 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground w-full sm:w-auto">
                  <TabsTrigger
                    value="calendar"
                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow"
                  >
                    <CalendarIcon className="h-4 w-4 mr-1.5" />
                    Calendario
                  </TabsTrigger>
                  <TabsTrigger
                    value="list"
                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow"
                  >
                    <ListBulletIcon className="h-4 w-4 mr-1.5" />
                    Lista
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Filtros de la lista */}
            {viewMode === 'list' && (
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={filters.status || 'all'}
                  onValueChange={(v) => setFilters((prev) => ({ ...prev, status: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger className="h-8 w-[150px]">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    {statusConfigs.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {canViewAllDates && mechanics.length > 0 && (
                  <Select
                    value={filters.mechanic || 'all'}
                    onValueChange={(v) => setFilters((prev) => ({ ...prev, mechanic: v === 'all' ? '' : v }))}
                  >
                    <SelectTrigger className="h-8 w-[170px]">
                      <SelectValue placeholder="Mecánico" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los mecánicos</SelectItem>
                      {mechanics.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {canViewAllDates && (
                  <>
                    <DatePicker
                      value={filters.dateFrom}
                      onChange={(v) => setFilters((prev) => ({ ...prev, dateFrom: v }))}
                      placeholder="Desde"
                      className="h-8 w-[140px]"
                    />
                    <DatePicker
                      value={filters.dateTo}
                      onChange={(v) => setFilters((prev) => ({ ...prev, dateTo: v }))}
                      placeholder="Hasta"
                      className="h-8 w-[140px]"
                    />
                  </>
                )}

                {hayFiltros && (
                  <button
                    onClick={limpiarFiltros}
                    className="inline-flex items-center h-8 rounded-lg px-3 text-sm font-medium border border-border text-muted-foreground hover:bg-accent transition-colors"
                  >
                    Limpiar
                  </button>
                )}
              </div>
            )}

            {showBulkActions && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <span className={`font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {selectedAppointments.size} turno{selectedAppointments.size !== 1 ? 's' : ''} seleccionado{selectedAppointments.size !== 1 ? 's' : ''}
                      </span>
                      <Button
                        variant="link"
                        onClick={clearSelection}
                        className={`h-auto px-0 font-medium ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'}`}
                      >
                        Deseleccionar todo
                      </Button>
                    </div>
                    <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-3">
                      <Select
                        value=""
                        onValueChange={(value) => {
                          if (value) {
                            handleBulkStatusChange(value);
                          }
                        }}
                      >
                        <SelectTrigger className="h-10 w-full sm:w-[200px]">
                          <SelectValue placeholder="Cambiar estado..." />
                        </SelectTrigger>
                        <SelectContent>
                          {statusConfigs.map((status) => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleBulkDelete}
                        className="h-10"
                      >
                        <TrashIcon className="mr-2 h-4 w-4" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Vista principal */}
            {viewMode === 'calendar' && renderCalendarView()}
            {viewMode === 'list' && renderListView()}

            {/* Formulario mejorado de turnos */}
            <AppointmentForm
              isOpen={showCreateModal}
              onClose={() => {
                setShowCreateModal(false);
                setEditAppointment(null);
              }}
              onSuccess={async () => {
                await fetchAppointments();
                setEditAppointment(null);
              }}
              appointment={editAppointment}
              mode={editAppointment ? 'edit' : 'create'}
            />

            {/* Completar/corregir datos del cliente (o vincular un "Sin datos" a un cliente real) */}
            <ClientDataDialog
              open={!!clientDataFor}
              onOpenChange={(o) => { if (!o) setClientDataFor(null); }}
              clientId={clientDataFor}
              onSaved={() => fetchAppointments({ silent: true })}
            />

            {/* Modal de detalles del turno */}
            <Dialog open={!!selectedAppointment} onOpenChange={(open) => !open && setSelectedAppointment(null)}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Detalles del Turno</DialogTitle>
                </DialogHeader>

                {selectedAppointment && (
                  <div className="space-y-5">
                    {/* Código de Turno */}
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                        Código de Turno
                      </h4>
                      <p className="text-base font-mono font-medium text-foreground">
                        {selectedAppointment.appointmentCode || '-'}
                      </p>
                    </div>
                    {/* Cliente */}
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                        Cliente
                      </h4>
                      <p className="text-base font-medium text-foreground">
                        {selectedAppointment.clientName}
                      </p>
                    </div>

                    {/* Vehículo */}
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                        Vehículo
                      </h4>
                      <p className="text-base text-foreground">
                        {selectedAppointment.vehiclePlate ? (
                          `${selectedAppointment.vehiclePlate} - ${selectedAppointment.vehicleBrand} ${selectedAppointment.vehicleModel}`
                        ) : (
                          <span className="text-muted-foreground">Sin vehículo</span>
                        )}
                      </p>
                    </div>

                    {/* Servicios */}
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">
                        Servicios
                      </h4>
                      <div className="space-y-1.5">
                        {selectedAppointment.serviceType.split(',').map((service, index) => (
                          <div key={index} className="flex items-start gap-2 text-sm text-foreground">
                            <span className="text-primary mt-1">â–¸</span>
                            <span>{service.trim()}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Productos */}
                    {selectedAppointment.products && selectedAppointment.products.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">
                          Productos
                        </h4>
                        <div className="space-y-1.5">
                          {selectedAppointment.products.map((product, index) => (
                            <div key={index} className="flex items-start gap-2 text-sm text-foreground">
                              <span className="text-primary mt-1">â–¸</span>
                              <span>
                                {product.name || product} {product.quantity && `(x${product.quantity})`}<span className="text-xs text-muted-foreground"> {` `}• Ubicacion: {product?.stock > 0 && product?.location ? product.location : `-`}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Fecha y Hora */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                          Fecha
                        </h4>
                        <p className="text-base text-foreground">
                          {new Date(selectedAppointment.scheduledDate).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                          Horario
                        </h4>
                        <p className="text-base text-foreground">
                          {selectedAppointment.timeSlot || '-'}
                        </p>
                      </div>
                    </div>

                    {/* Estado */}
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">
                        Estado
                      </h4>
                      <Badge
                        className="text-xs font-medium"
                        style={{
                          backgroundColor: selectedAppointment.statusConfig?.bgColor,
                          color: selectedAppointment.statusConfig?.textColor
                        }}
                      >
                        {selectedAppointment.statusConfig?.label}
                      </Badge>
                    </div>

                    {/* Notas */}
                    {selectedAppointment.notes && (
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                          Notas
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {selectedAppointment.notes}
                        </p>
                      </div>
                    )}

                    {/* Costo */}
                    {canViewPrices && selectedAppointment.totalCost && (
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                          Costo Total
                        </h4>
                        <p className="text-2xl font-bold text-foreground">
                          ${selectedAppointment.totalCost.toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (selectedAppointment) {
                        setInformeOtId(selectedAppointment.id);
                      }
                    }}
                  >
                    <DocumentTextIcon className="mr-2 h-4 w-4" />
                    Informe de servicio
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (selectedAppointment) {
                        const appointmentToEdit = {
                          ...selectedAppointment,
                          products: selectedAppointment.products || []
                        };
                        setEditAppointment(appointmentToEdit);
                        setShowCreateModal(true);
                        setSelectedAppointment(null);
                      }
                    }}
                  >
                    <PencilIcon className="mr-2 h-4 w-4" />
                    Editar Turno
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      ) : (
        // Si solo tiene permiso de agenda (sin tabs principales)
        <>
          {/* Buscador */}
          <div className="relative">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
            <Input
              type="text"
              placeholder="Buscar por código, cliente, patente..."
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground bg-background dark:bg-input/30 border-input flex min-w-0 rounded-lg border px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive h-8 w-full sm:w-[150px] lg:w-[250px] pl-8"
            />
          </div>

          {showBulkActions && (
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span className={`font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {selectedAppointments.size} turno{selectedAppointments.size !== 1 ? 's' : ''} seleccionado{selectedAppointments.size !== 1 ? 's' : ''}
                    </span>
                    <Button
                      variant="link"
                      onClick={clearSelection}
                      className={`h-auto px-0 font-medium ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'}`}
                    >
                      Deseleccionar todo
                    </Button>
                  </div>
                  <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-3">
                    <Select
                      value=""
                      onValueChange={(value) => {
                        if (value) {
                          handleBulkStatusChange(value);
                        }
                      }}
                    >
                      <SelectTrigger className="h-10 w-full sm:w-[200px]">
                        <SelectValue placeholder="Cambiar estado..." />
                      </SelectTrigger>
                      <SelectContent>
                        {statusConfigs.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDelete}
                      className="h-10"
                    >
                      <TrashIcon className="mr-2 h-4 w-4" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Vista principal */}
          {viewMode === 'calendar' && renderCalendarView()}
          {viewMode === 'list' && renderListView()}

          {/* Formulario mejorado de turnos */}
          <AppointmentForm
            isOpen={showCreateModal}
            onClose={() => {
              setShowCreateModal(false);
              setEditAppointment(null);
            }}
            onSuccess={async () => {
              // Actualizar datos
              await fetchAppointments();
              setEditAppointment(null);
            }}
            appointment={editAppointment}
            mode="edit"
          />

          {/* Modal de detalles del turno */}
          <Dialog open={!!selectedAppointment} onOpenChange={(open) => !open && setSelectedAppointment(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Detalles del Turno</DialogTitle>
              </DialogHeader>

              {selectedAppointment && (
                <div className="space-y-5">
                  {/* Código de Turno */}
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                      Código de Turno
                    </h4>
                    <p className="text-base font-mono font-medium text-foreground">
                      {selectedAppointment.appointmentCode || '-'}
                    </p>
                  </div>
                  {/* Cliente */}
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                      Cliente
                    </h4>
                    <p className="text-base font-medium text-foreground">
                      {selectedAppointment.clientName}
                    </p>
                  </div>

                  {/* Vehículo */}
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                      Vehículo
                    </h4>
                    <p className="text-base text-foreground">
                      {selectedAppointment.vehiclePlate ? (
                        `${selectedAppointment.vehiclePlate} - ${selectedAppointment.vehicleBrand} ${selectedAppointment.vehicleModel}`
                      ) : (
                        <span className="text-muted-foreground">Sin vehículo</span>
                      )}
                    </p>
                  </div>

                  {/* Servicios */}
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">
                      Servicios
                    </h4>
                    <div className="space-y-1.5">
                      {selectedAppointment.serviceType.split(',').map((service, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm text-foreground">
                          <span className="text-primary mt-1">â–¸</span>
                          <span>{service.trim()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Productos */}
                  {selectedAppointment.products && selectedAppointment.products.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">
                        Productos
                      </h4>
                      <div className="space-y-1.5">
                        {selectedAppointment.products.map((product, index) => (
                          <div key={index} className="flex items-start gap-2 text-sm text-foreground">
                            <span className="text-primary mt-1">â–¸</span>
                            <span>
                              {product.name || product} {product.quantity && `(x${product.quantity})`}<span className="text-xs text-muted-foreground"> {` `}• Ubicacion: {product?.stock > 0 && product?.location ? product.location : `-`}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fecha y Hora */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                        Fecha
                      </h4>
                      <p className="text-base text-foreground">
                        {new Date(selectedAppointment.scheduledDate).toLocaleDateString('es-ES', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                        Horario
                      </h4>
                      <p className="text-base text-foreground">
                        {selectedAppointment.timeSlot || '-'}
                      </p>
                    </div>
                  </div>

                  {/* Estado */}
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">
                      Estado
                    </h4>
                    <Badge
                      className="text-xs font-medium"
                      style={{
                        backgroundColor: selectedAppointment.statusConfig?.bgColor,
                        color: selectedAppointment.statusConfig?.textColor
                      }}
                    >
                      {selectedAppointment.statusConfig?.label}
                    </Badge>
                  </div>

                  {/* Notas */}
                  {selectedAppointment.notes && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                        Notas
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {selectedAppointment.notes}
                      </p>
                    </div>
                  )}

                  {/* Costo */}
                  {canViewPrices && selectedAppointment.totalCost && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                        Costo Total
                      </h4>
                      <p className="text-2xl font-bold text-foreground">
                        ${selectedAppointment.totalCost.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (selectedAppointment) {
                      setInformeOtId(selectedAppointment.id);
                    }
                  }}
                >
                  <DocumentTextIcon className="mr-2 h-4 w-4" />
                  Informe de servicio
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (selectedAppointment) {
                      const appointmentToEdit = {
                        ...selectedAppointment,
                        products: selectedAppointment.products || []
                      };
                      setEditAppointment(appointmentToEdit);
                      setShowCreateModal(true);
                      setSelectedAppointment(null);
                    }
                  }}
                >
                  <PencilIcon className="mr-2 h-4 w-4" />
                  Editar Turno
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        </>
      )}

      {dialogoRetiro}

      <InformeServicioDialog
        appointmentId={informeOtId}
        open={!!informeOtId}
        onOpenChange={(v) => { if (!v) setInformeOtId(null); }}
      />


      </div>
    );
};

export default AppointmentsEnhanced; 




